import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
  LinearProgress,
  TextField,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
  Grid
} from '@mui/material'
import {
  Visibility,
  MoreVert,
  Lock,
  LockOpen,
  Print,
  History,
  Refresh,
  Search,
  FilterList,
  Download
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { format } from 'date-fns'
import closureService from '../../../services/closureService'

const ClosureList = ({ onRefresh }) => {
  const { enqueueSnackbar } = useSnackbar()
  
  const [loading, setLoading] = useState(false)
  const [closures, setClosures] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedClosure, setSelectedClosure] = useState(null)
  const [filters, setFilters] = useState({
    search: '',
    closureReason: '',
    isLocked: '',
    startDate: '',
    endDate: ''
  })

  useEffect(() => {
    loadClosures()
  }, [page, rowsPerPage, filters])

  const loadClosures = async () => {
    setLoading(true)
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...filters
      }
      
      const response = await closureService.getClosures(params)
      
      if (response.data.success) {
        setClosures(response.data.data.docs || [])
        setTotalCount(response.data.data.totalDocs || 0)
      }
    } catch (error) {
      enqueueSnackbar('Failed to load closures', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleMenuOpen = (event, closure) => {
    setAnchorEl(event.currentTarget)
    setSelectedClosure(closure)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedClosure(null)
  }

  const handleViewDetails = (closure) => {
    // Navigate to closure details or open modal
    console.log('View details:', closure)
    handleMenuClose()
  }

  const handlePrintSheet = async (closure) => {
    try {
      const response = await closureService.printClosureSheet(closure.mrdFile._id)
      if (response.data.success) {
        // Handle print functionality
        console.log('Print data:', response.data.data)
        enqueueSnackbar('Closure sheet generated', { variant: 'success' })
      }
    } catch (error) {
      enqueueSnackbar('Failed to generate closure sheet', { variant: 'error' })
    }
    handleMenuClose()
  }

  const handleViewAudit = async (closure) => {
    try {
      const response = await closureService.getAuditTrail(closure._id)
      if (response.data.success) {
        // Show audit trail in modal or navigate to audit page
        console.log('Audit trail:', response.data.data)
      }
    } catch (error) {
      enqueueSnackbar('Failed to load audit trail', { variant: 'error' })
    }
    handleMenuClose()
  }

  const handleLockToggle = async (closure) => {
    try {
      if (closure.isLocked) {
        const reason = prompt('Enter reason for unlocking:')
        if (reason) {
          await closureService.unlockClosure(closure._id, { reason })
          enqueueSnackbar('Closure unlocked successfully', { variant: 'success' })
        }
      } else {
        await closureService.lockClosure(closure._id)
        enqueueSnackbar('Closure locked successfully', { variant: 'success' })
      }
      loadClosures()
    } catch (error) {
      enqueueSnackbar('Failed to update lock status', { variant: 'error' })
    }
    handleMenuClose()
  }

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
    setPage(0) // Reset to first page when filtering
  }

  const resetFilters = () => {
    setFilters({
      search: '',
      closureReason: '',
      isLocked: '',
      startDate: '',
      endDate: ''
    })
    setPage(0)
  }

  const getStatusChip = (closure) => {
    if (closure.isLocked) {
      return <Chip label="Locked" color="error" size="small" icon={<Lock />} />
    } else {
      return <Chip label="Unlocked" color="warning" size="small" icon={<LockOpen />} />
    }
  }

  const getClosureReasonColor = (reason) => {
    switch (reason) {
      case 'COMPLETED': return 'success'
      case 'DISCHARGE_CLOSURE': return 'primary'
      case 'TRANSFER_CLOSURE': return 'info'
      case 'DEATH_CLOSURE': return 'error'
      case 'ADMINISTRATIVE_CLOSURE': return 'warning'
      default: return 'default'
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          Closed Files ({totalCount})
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => {
            loadClosures()
            if (onRefresh) onRefresh()
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search files..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Closure Reason</InputLabel>
              <Select
                value={filters.closureReason}
                label="Closure Reason"
                onChange={(e) => handleFilterChange('closureReason', e.target.value)}
              >
                <SelectMenuItem value="">All</SelectMenuItem>
                <SelectMenuItem value="COMPLETED">Completed</SelectMenuItem>
                <SelectMenuItem value="DISCHARGE_CLOSURE">Discharge</SelectMenuItem>
                <SelectMenuItem value="TRANSFER_CLOSURE">Transfer</SelectMenuItem>
                <SelectMenuItem value="DEATH_CLOSURE">Death</SelectMenuItem>
                <SelectMenuItem value="ADMINISTRATIVE_CLOSURE">Administrative</SelectMenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Lock Status</InputLabel>
              <Select
                value={filters.isLocked}
                label="Lock Status"
                onChange={(e) => handleFilterChange('isLocked', e.target.value)}
              >
                <SelectMenuItem value="">All</SelectMenuItem>
                <SelectMenuItem value="true">Locked</SelectMenuItem>
                <SelectMenuItem value="false">Unlocked</SelectMenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start Date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="End Date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <Button
              variant="outlined"
              onClick={resetFilters}
              startIcon={<FilterList />}
              fullWidth
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Closures Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>File Number</TableCell>
              <TableCell>Patient Name</TableCell>
              <TableCell>Closure Date</TableCell>
              <TableCell>Closure Reason</TableCell>
              <TableCell>Closed By</TableCell>
              <TableCell>TAT (Days)</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {closures.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Alert severity="info">
                    No closed files found
                  </Alert>
                </TableCell>
              </TableRow>
            ) : (
              closures.map((closure) => (
                <TableRow key={closure._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {closure.mrdFile?.fileNumber}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {closure.mrdFile?.patientName}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {closure.closedAt ? format(new Date(closure.closedAt), 'MMM dd, yyyy') : '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {closure.closedAt ? format(new Date(closure.closedAt), 'HH:mm') : ''}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Chip
                      label={closure.closureReason?.replace(/_/g, ' ') || 'N/A'}
                      size="small"
                      color={getClosureReasonColor(closure.closureReason)}
                      variant="outlined"
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {closure.closedBy?.name || 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {closure.closedBy?.designation}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {closure.closureAgeInDays || 0} days
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    {getStatusChip(closure)}
                  </TableCell>
                  
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(closure)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Print Sheet">
                        <IconButton
                          size="small"
                          onClick={() => handlePrintSheet(closure)}
                        >
                          <Print fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, closure)}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleViewDetails(selectedClosure)}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => handleViewAudit(selectedClosure)}>
          <ListItemIcon>
            <History fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Audit Trail</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => handlePrintSheet(selectedClosure)}>
          <ListItemIcon>
            <Print fontSize="small" />
          </ListItemIcon>
          <ListItemText>Print Closure Sheet</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => handleLockToggle(selectedClosure)}>
          <ListItemIcon>
            {selectedClosure?.isLocked ? <LockOpen fontSize="small" /> : <Lock fontSize="small" />}
          </ListItemIcon>
          <ListItemText>
            {selectedClosure?.isLocked ? 'Unlock Closure' : 'Lock Closure'}
          </ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  )
}

export default ClosureList